<template>
  <div class="page-main-box pt-24 pr-16">
    <!-- 有数据 -->
    <div class="pl-16">
      <!-- 搜索头 -->
      <search-header :schemaData="schema" @getDataList="handleFormSearch" />

      <!-- 列表 -->
      <div class="table-height" :style="{ height: tableHeight + 'px' }">
        <p-table id="account-table" :dataSource="tableDataSource" height="auto">
          <p-t-column type="seq" width="60" title="序号" />
          <p-t-column field="order_no" min-width="240" title="订单编号" />

          <p-t-column field="oil_name" min-width="146" title="油品类型" />
          <p-t-column field="driver_name" min-width="128" title="司机姓名" />
          <p-t-column field="driver_mobile" min-width="146" title="手机号码" />
          <p-t-column title="创建时间" field="order_time" min-width="183">
            <template v-slot="{ row }">
              {{
                row.order_time &&
                  formatToDateTime(row.order_time, "yyyy-MM-dd hh:mm:ss")
              }}
            </template>
          </p-t-column>
          <p-t-column field="station_name" min-width="260" title="加油站名称">
            <template v-slot="{ row }">
              <span>{{
                row.station_name?.length > 15
                  ? row.station_name?.substr(0, 15) + "..."
                  : row.station_name
              }}</span>
            </template>
          </p-t-column>
          <p-t-column field="sp_name" min-width="118" title="服务商" />
          <p-t-column
            field="amount"
            min-width="122"
            title="订单金额"
            align="right"
          >
            <template v-slot="{ row }">
              <span>{{ (row.amount / 100).toFixed(2) }}</span>
            </template>
          </p-t-column>
          <p-t-column
            field="quantity"
            min-width="130"
            title="加油/气量"
            align="right"
          />
          <p-t-column field="status_desc" min-width="126" title="状态">
            <template v-slot="{ row }">
              <Badge
                v-if="row.status_desc === '待支付'"
                color="#ff9800"
                :text="row.status_desc"
              ></Badge>
              <Badge
                v-if="
                  row.status_desc === '待支付' ||
                    row.status_desc === '已取消' ||
                    row.status_desc === '已退款'
                "
                color="#c1c1c1"
                :text="row.status_desc"
              ></Badge>
              <Badge
                v-if="row.status_desc === '已完成'"
                color="#00BF5F"
                :text="row.status_desc"
              ></Badge>
            </template>
          </p-t-column>
          <p-t-column width="60" title="操作" fixed="right">
            <template v-slot="{ row }">
              <Button type="link" @click="handleToDetail(row)">
                详情
              </Button>
            </template>
          </p-t-column>

          <template #footer>
            <a-button
              v-if="$store.getters.roles.includes('ConsumerOrdersExport')"
              class="mt-12 ml-32"
              @click="handleExport"
              type="primary"
              :loading="isLoading"
              >导出</a-button
            >
          </template>
        </p-table>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, toRaw, onMounted } from "vue";
import { Button, Badge } from "ant-design-vue";
import { useRoute, useRouter } from "vue-router";
import { Table } from "@wlhy-web-components/common";
import { searchHeaderOptions } from "./Constants";
import { useGasOrderPageApi, useGasOrderPageExportApi } from "@/apis/oil";
import { useServiceProvideListEnumApi } from "@/apis/enums";
import $await from "@/utils/await";
import useTableDataRecords from "@/hooks/useTableDataRecords";
import useSearchForm from "@/hooks/UseSearchForm";
import useSearch from "@/hooks/useSearch";
import useTableHeight from "@/hooks/useTableHeight";
import { formatToDateTime } from "@wlhy-web-lib/shared";
import { exportExcel } from "@/hooks/useExport";

export default {
  name: "consumer-orders",
  components: { Button, Badge },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const gasOrderPageApi = useGasOrderPageApi();
    const gasOrderPageExportApi = useGasOrderPageExportApi();
    const serviceProvideListEnumApi = useServiceProvideListEnumApi();

    // table高度
    const tableHeight = ref();

    const isLoading = ref(false);

    const tableDataSource = reactive({
      loading: false,
      data: [],
      error: "",
      meta: {
        total: 0,
        pageSize: 20,
        currentPageNo: 1
      },
      sum: {
        data: [],
        loading: false,
        error: ""
      }
    });

    let schemaList = searchHeaderOptions;

    (async () => {
      let [, sp_code] = await $await(serviceProvideListEnumApi());
      schemaList = useSearch(searchHeaderOptions, sp_code, "sp_code");
    })();

    const { filter, handleFormSearch } = useSearchForm(schemaList);

    Table.useTableConfig({
      filter,
      dataSource: tableDataSource,
      async fetchData() {
        const data = {
          ...toRaw(filter.value),
          driver_ids: route.query.driver_id ? [route.query.driver_id] : [],
          company_id: route.query.id,
          card_no: route.query.card_no,
          size: tableDataSource.meta.pageSize,
          page: tableDataSource.meta.currentPageNo,
          sp_code: filter.value.sp_code
            ? filter.value.sp_code
            : route.query.sp_code
        };
        const [, res] = await $await(gasOrderPageApi(data));
        return useTableDataRecords(res);
      }
    });

    // 跳转到详情页
    const handleToDetail = row => {
      router.push({
        path: "/oil/consumer-orders-detail",
        query: {
          id: row.id
        }
      });
    };

    // 导出
    const handleExport = async () => {
      isLoading.value = true;
      const data = {
        ...toRaw(filter.value),
        driver_ids: route.query.driver_id ? [route.query.driver_id] : [],
        company_id: route.query.id,
        card_no: route.query.card_no,
        sp_code: filter.value.sp_code
          ? filter.value.sp_code
          : route.query.sp_code
      };
      const [, res] = await $await(gasOrderPageExportApi(data));
      exportExcel(res, "消费订单");
      isLoading.value = false;
    };

    onMounted(() => {
      tableHeight.value = useTableHeight();
    });

    return {
      tableHeight,
      handleToDetail,
      tableDataSource,
      handleFormSearch,
      schema: searchHeaderOptions,
      formatToDateTime,
      isLoading,
      handleExport
    };
  }
};
</script>

<style lang="less" scoped></style>
